/**
 * Individual chat message bubble.
 *
 * User messages are right-aligned with a primary accent; assistant messages
 * are left-aligned with a subtle surface. Content is rendered as Markdown
 * (GFM); raw HTML is ignored by react-markdown (safe for LLM-sourced text).
 */

import { memo } from 'react';
import { Box } from '@mui/material';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { MONOSPACE_FONT_FAMILY } from './ProposalCardShell';

export type ChatBubbleRole = 'user' | 'assistant';

interface ChatMessageProps {
    role: ChatBubbleRole;
    content: string;
}

// Memoised: during streaming the list re-renders on every token; finalised
// bubbles must not re-run the markdown pipeline.
function ChatMessage({ role, content }: ChatMessageProps) {
    const isUser = role === 'user';
    const codeBg = isUser ? 'rgba(255,255,255,0.18)' : 'rgba(0,0,0,0.08)';

    return (
        <Box
            display="flex"
            justifyContent={isUser ? 'flex-end' : 'flex-start'}
            mb={1.5}
        >
            <Box
                sx={(theme) => ({
                    px: 1.5,
                    py: 1,
                    maxWidth: '85%',
                    borderRadius: '12px',
                    ...(isUser
                        ? {
                              borderBottomRightRadius: '4px',
                              backgroundColor: theme.palette.primary.dark,
                              color: '#fff',
                          }
                        : {
                              borderBottomLeftRadius: '4px',
                              backgroundColor:
                                  theme.palette.mode === 'dark'
                                      ? 'rgba(255,255,255,0.06)'
                                      : 'rgba(0,0,0,0.04)',
                              border: `1px solid ${theme.palette.divider}`,
                          }),
                })}
            >
                <Box
                    sx={{
                        fontSize: 12,
                        lineHeight: 1.5,
                        wordBreak: 'break-word',
                        '& > :first-of-type': { mt: 0 },
                        '& > :last-child': { mb: 0 },
                        '& p': { my: 0.5 },
                        '& ul, & ol': { my: 0.5, pl: 2.5 },
                        '& li': { mb: 0.25 },
                        '& code': {
                            fontFamily: MONOSPACE_FONT_FAMILY,
                            fontSize: '0.9em',
                            backgroundColor: codeBg,
                            px: 0.5,
                            py: 0.125,
                            borderRadius: 0.5,
                        },
                        '& pre': {
                            backgroundColor: codeBg,
                            p: 1,
                            borderRadius: 1,
                            overflow: 'auto',
                            my: 0.5,
                            '& code': {
                                backgroundColor: 'transparent',
                                px: 0,
                                py: 0,
                            },
                        },
                    }}
                >
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                        {content}
                    </ReactMarkdown>
                </Box>
            </Box>
        </Box>
    );
}

export default memo(ChatMessage);
